import { useState, useEffect, useCallback } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/utils/api";
import { toast } from "sonner";
import {
  Settings, Building2, FileText, Banknote, Loader2, Eye, EyeOff,
  Check, Plus, X, AlertCircle,
} from "lucide-react";

const TABS = [
  { key: "pricing", label: "Pricing Defaults", icon: FileText },
  { key: "bank", label: "Bank Details", icon: Banknote },
  { key: "gstins", label: "Company GSTINs", icon: Building2 },
  { key: "platform", label: "Platform Settings", icon: Settings },
];

export default function AdminConfig() {
  const [tab, setTab] = useState("pricing");

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="admin-config-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">System Configuration</h1>
          <p className="text-sm text-slate-500">
            Manage pricing defaults, bank details, GSTINs and platform settings
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-1.5 transition ${
                  active
                    ? "bg-slate-800 text-white"
                    : "bg-white border border-slate-300 text-slate-700 hover:border-slate-400"
                }`}
                data-testid={`config-tab-${t.key}`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        {tab === "pricing" && <PricingDefaultsTab />}
        {tab === "bank" && <BankDetailsTab />}
        {tab === "gstins" && <CompanyGstinsTab />}
        {tab === "platform" && <PlatformSettingsTab />}
      </div>
    </DashboardLayout>
  );
}

// ─── TAB 1: PRICING ──────────────────────────────────────────────────────
function PricingDefaultsTab() {
  const [fmlm, setFmlm] = useState(null);
  const [tpl, setTpl] = useState(null);
  const [platformFee, setPlatformFee] = useState(null);
  const [demurrage, setDemurrage] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b, c, d] = await Promise.allSettled([
        api.get("/v2/admin-config/fm-lm-rates"),
        api.get("/v2/admin-config/tpl-markup"),
        api.get("/v2/admin-config/platform_fee_config"),
        api.get("/v2/admin-config/demurrage"),
      ]);
      if (a.status === "fulfilled") setFmlm(a.value.data);
      if (b.status === "fulfilled") setTpl(b.value.data);
      if (c.status === "fulfilled") setPlatformFee(c.value.data?.value || {});
      if (d.status === "fulfilled") setDemurrage(d.value.data?.value || {});
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveFmLm = async () => {
    try {
      await api.put("/v2/admin-config/fm-lm-rates", { fm: fmlm.fm, lm: fmlm.lm });
      toast.success("FM/LM rates saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const saveTpl = async () => {
    try {
      await api.put("/v2/admin-config/tpl-markup", { markup_pct: Number(tpl.markup_pct) });
      toast.success("3PL markup saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const savePlatformFee = async () => {
    try {
      await api.put("/v2/admin-config/platform_fee_config", { value: platformFee });
      toast.success("Platform fee saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const saveDemurrage = async () => {
    try {
      await api.put("/v2/admin-config/demurrage", { value: demurrage });
      toast.success("Demurrage saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  if (loading) return <Loading />;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* FM */}
      {fmlm && (
        <Card title="First Mile" testid="card-fm">
          {["rate_per_kg", "free_km", "rate_per_km_beyond", "min_charge"].map((k) => (
            <NumField key={k} label={k} value={fmlm.fm?.[k]} onChange={(v) => setFmlm({ ...fmlm, fm: { ...fmlm.fm, [k]: v } })} testid={`fm-${k}`} />
          ))}
          <Button onClick={saveFmLm} className="bg-slate-800 hover:bg-slate-900 text-white mt-2" data-testid="save-fm-btn">
            <Check className="w-4 h-4 mr-1" /> Save FM/LM
          </Button>
        </Card>
      )}
      {/* LM */}
      {fmlm && (
        <Card title="Last Mile" testid="card-lm">
          {["rate_per_kg", "free_km", "rate_per_km_beyond", "min_charge"].map((k) => (
            <NumField key={k} label={k} value={fmlm.lm?.[k]} onChange={(v) => setFmlm({ ...fmlm, lm: { ...fmlm.lm, [k]: v } })} testid={`lm-${k}`} />
          ))}
        </Card>
      )}
      {/* TPL Markup */}
      {tpl && (
        <Card title="3PL Markup" testid="card-tpl">
          <NumField label="Markup %" value={tpl.markup_pct} onChange={(v) => setTpl({ ...tpl, markup_pct: v })} testid="tpl-markup" />
          <Button onClick={saveTpl} className="bg-slate-800 hover:bg-slate-900 text-white" data-testid="save-tpl-btn">
            <Check className="w-4 h-4 mr-1" /> Save
          </Button>
        </Card>
      )}
      {/* Platform Fee */}
      {platformFee && (
        <Card title="Platform Fee" testid="card-platform-fee">
          <NumField label="Amount (₹)" value={platformFee.amount} onChange={(v) => setPlatformFee({ ...platformFee, amount: v })} testid="pf-amount" />
          <ToggleRow label="Enabled" checked={!!platformFee.enabled} onChange={(v) => setPlatformFee({ ...platformFee, enabled: v })} testid="pf-enabled" />
          <Button onClick={savePlatformFee} className="bg-slate-800 hover:bg-slate-900 text-white" data-testid="save-platform-fee-btn">
            <Check className="w-4 h-4 mr-1" /> Save
          </Button>
        </Card>
      )}
      {/* Demurrage */}
      {demurrage && (
        <Card title="Demurrage" testid="card-demurrage" wide>
          <div className="grid grid-cols-2 gap-3">
            {["flat_minimum", "lm_multiplier", "per_kg_per_day", "free_attempts"].map((k) => (
              <NumField key={k} label={k} value={demurrage[k]} onChange={(v) => setDemurrage({ ...demurrage, [k]: v })} testid={`dem-${k}`} />
            ))}
          </div>
          <Button onClick={saveDemurrage} className="bg-slate-800 hover:bg-slate-900 text-white mt-2" data-testid="save-demurrage-btn">
            <Check className="w-4 h-4 mr-1" /> Save
          </Button>
        </Card>
      )}
    </div>
  );
}

// ─── TAB 2: BANK ──────────────────────────────────────────────────────────
function BankDetailsTab() {
  const [bank, setBank] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revealAcc, setRevealAcc] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/v2/admin-config/bank_details");
        setBank(r.data?.value || {});
      } catch (e) {
        toast.error("Failed to load bank details");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    try {
      await api.put("/v2/admin-config/bank_details", { value: bank });
      toast.success("Bank details saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  if (loading) return <Loading />;
  const acc = bank.account_number || "";
  const masked = acc ? acc.slice(0, 2) + "X".repeat(Math.max(acc.length - 6, 0)) + acc.slice(-4) : "";

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3 max-w-2xl">
      <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded px-3 py-2 flex gap-2">
        <AlertCircle className="w-4 h-4 flex-shrink-0" />
        These appear on all generated invoices.
      </div>
      <TextField label="Bank Name" value={bank.bank_name} onChange={(v) => setBank({ ...bank, bank_name: v })} testid="bank-name" />
      <TextField label="Account Holder" value={bank.account_holder} onChange={(v) => setBank({ ...bank, account_holder: v })} testid="account-holder" />
      <div>
        <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Account Number</label>
        <div className="flex gap-2">
          <Input
            type={revealAcc ? "text" : "password"}
            value={revealAcc ? acc : masked}
            onChange={(e) => setBank({ ...bank, account_number: e.target.value })}
            data-testid="account-number"
          />
          <Button variant="outline" type="button" onClick={() => setRevealAcc((s) => !s)} data-testid="toggle-acc-reveal">
            {revealAcc ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </Button>
        </div>
      </div>
      <TextField label="IFSC Code" value={bank.ifsc_code} onChange={(v) => setBank({ ...bank, ifsc_code: v.toUpperCase() })} testid="ifsc" />
      <TextField label="Branch" value={bank.branch} onChange={(v) => setBank({ ...bank, branch: v })} testid="branch" />
      <TextField label="UPI ID" value={bank.upi_id} onChange={(v) => setBank({ ...bank, upi_id: v })} testid="upi" />
      <Button onClick={save} className="bg-slate-800 hover:bg-slate-900 text-white" data-testid="save-bank-btn">
        <Check className="w-4 h-4 mr-1" /> Save Bank Details
      </Button>
    </div>
  );
}

// ─── TAB 3: COMPANY GSTINS ───────────────────────────────────────────────
function CompanyGstinsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [newG, setNewG] = useState({ gstin: "", state_code: "", state_name: "", legal_name: "", address: "", pan: "", is_primary: false });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/v2/company/gstins");
      setItems(r.data || []);
    } catch (e) {
      toast.error("Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (g, active) => {
    try {
      await api.put(`/v2/company/gstins/${g.gstin}/${active ? "activate" : "deactivate"}`);
      toast.success(`GSTIN ${active ? "activated" : "deactivated"}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const setPrimary = async (g) => {
    try {
      await api.put(`/v2/company/gstins/${g.gstin}`, { is_primary: true });
      toast.success("Primary updated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submitNew = async () => {
    if (!newG.gstin || !newG.state_code || !newG.legal_name) {
      toast.error("GSTIN, state code and legal name required");
      return;
    }
    try {
      await api.post("/v2/company/gstins", newG);
      toast.success("GSTIN added");
      setAddOpen(false);
      setNewG({ gstin: "", state_code: "", state_name: "", legal_name: "", address: "", pan: "", is_primary: false });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (loading) return <Loading />;
  return (
    <div className="space-y-3">
      <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded px-3 py-2 flex gap-2">
        <AlertCircle className="w-4 h-4 flex-shrink-0" />
        GST on invoices is auto-determined by customer state vs company GSTIN state.
      </div>
      <div className="flex justify-end">
        <Button onClick={() => setAddOpen(true)} className="bg-slate-800 hover:bg-slate-900 text-white" data-testid="add-gstin-btn">
          <Plus className="w-4 h-4 mr-1" /> Add GSTIN
        </Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm" data-testid="gstins-table">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left p-3">GSTIN</th>
              <th className="text-left p-3">State</th>
              <th className="text-left p-3">Legal Name</th>
              <th className="text-center p-3">Status</th>
              <th className="text-center p-3">Primary</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((g) => (
              <tr key={g.gstin} className="hover:bg-slate-50">
                <td className="p-3 font-mono text-xs font-semibold">{g.gstin}</td>
                <td className="p-3 text-xs">{g.state_code} — {g.state_name}</td>
                <td className="p-3">{g.legal_name}</td>
                <td className="p-3 text-center">
                  <span className={`inline-block text-[10px] font-bold px-2 py-1 rounded border ${
                    g.is_active
                      ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                      : "bg-red-100 text-red-700 border-red-200"
                  }`}>
                    {g.is_active ? "ACTIVE" : "INACTIVE"}
                  </span>
                </td>
                <td className="p-3 text-center">
                  {g.is_primary ? (
                    <span className="inline-block text-[10px] font-bold px-2 py-1 rounded border bg-blue-100 text-blue-700 border-blue-200">
                      PRIMARY
                    </span>
                  ) : (
                    <button onClick={() => setPrimary(g)} className="text-xs text-blue-600 hover:underline" data-testid={`set-primary-${g.gstin}`}>
                      Set primary
                    </button>
                  )}
                </td>
                <td className="p-3 text-right">
                  <button
                    onClick={() => toggle(g, !g.is_active)}
                    className={`text-xs ${g.is_active ? "text-red-600" : "text-emerald-600"} hover:underline`}
                    data-testid={`toggle-${g.gstin}`}
                  >
                    {g.is_active ? "Deactivate" : "Activate"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {addOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setAddOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl p-5 w-full max-w-lg shadow-2xl">
            <h3 className="text-lg font-bold text-slate-800 mb-3">Add Company GSTIN</h3>
            <div className="grid grid-cols-2 gap-3">
              <TextField label="GSTIN" value={newG.gstin} onChange={(v) => setNewG({ ...newG, gstin: v.toUpperCase() })} testid="new-gstin" />
              <TextField label="State Code" value={newG.state_code} onChange={(v) => setNewG({ ...newG, state_code: v })} testid="new-state-code" />
              <TextField label="State Name" value={newG.state_name} onChange={(v) => setNewG({ ...newG, state_name: v })} testid="new-state-name" />
              <TextField label="PAN" value={newG.pan} onChange={(v) => setNewG({ ...newG, pan: v.toUpperCase() })} testid="new-pan" />
              <div className="col-span-2">
                <TextField label="Legal Name" value={newG.legal_name} onChange={(v) => setNewG({ ...newG, legal_name: v })} testid="new-legal" />
              </div>
              <div className="col-span-2">
                <TextField label="Address" value={newG.address} onChange={(v) => setNewG({ ...newG, address: v })} testid="new-address" />
              </div>
              <ToggleRow label="Set as primary" checked={newG.is_primary} onChange={(v) => setNewG({ ...newG, is_primary: v })} testid="new-primary" />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button onClick={submitNew} className="bg-slate-800 hover:bg-slate-900 text-white" data-testid="submit-new-gstin">
                Add GSTIN
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── TAB 4: PLATFORM ──────────────────────────────────────────────────────
function PlatformSettingsTab() {
  const [handling, setHandling] = useState(null);
  const [lr, setLr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [h, l] = await Promise.allSettled([
          api.get("/v2/admin-config/handling_config"),
          api.get("/v2/admin-config/lr_charge"),
        ]);
        if (h.status === "fulfilled") setHandling(h.value.data?.value || {});
        if (l.status === "fulfilled") setLr(l.value.data?.value || {});
      } finally { setLoading(false); }
    })();
  }, []);

  const saveHandling = async () => {
    try {
      await api.put("/v2/admin-config/handling_config", { value: handling });
      toast.success("Handling saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const saveLr = async () => {
    try {
      await api.put("/v2/admin-config/lr_charge", { value: lr });
      toast.success("LR charge saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (loading) return <Loading />;
  return (
    <div className="space-y-4">
      <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded px-3 py-2 flex gap-2">
        <AlertCircle className="w-4 h-4 flex-shrink-0" />
        Changes take effect on new bookings only. Existing invoices are not affected.
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {handling && (
          <Card title="Handling" testid="card-handling">
            <NumField label="Threshold (kg)" value={handling.threshold_kg} onChange={(v) => setHandling({ ...handling, threshold_kg: v })} testid="handling-threshold" />
            <NumField label="Rate per kg (₹)" value={handling.rate_per_kg} onChange={(v) => setHandling({ ...handling, rate_per_kg: v })} testid="handling-rate" />
            <Button onClick={saveHandling} className="bg-slate-800 hover:bg-slate-900 text-white" data-testid="save-handling-btn">
              <Check className="w-4 h-4 mr-1" /> Save
            </Button>
          </Card>
        )}
        {lr && (
          <Card title="LR Charge (Own Network)" testid="card-lr">
            <NumField label="Amount (₹)" value={lr.own_network} onChange={(v) => setLr({ ...lr, own_network: v })} testid="lr-own-network" />
            <Button onClick={saveLr} className="bg-slate-800 hover:bg-slate-900 text-white" data-testid="save-lr-btn">
              <Check className="w-4 h-4 mr-1" /> Save
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
}

// ─── Small components ────────────────────────────────────────────────────
function Card({ title, children, testid, wide }) {
  return (
    <div className={`bg-white border border-slate-200 rounded-xl p-4 space-y-3 ${wide ? "md:col-span-2" : ""}`} data-testid={testid}>
      <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">{title}</h3>
      {children}
    </div>
  );
}
function NumField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold text-slate-600 uppercase mb-1">{label}</label>
      <Input type="number" step="0.01" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} data-testid={testid} />
    </div>
  );
}
function TextField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold text-slate-600 uppercase mb-1">{label}</label>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}
function ToggleRow({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center justify-between gap-3 py-1.5 cursor-pointer" data-testid={testid}>
      <span className="text-sm text-slate-700">{label}</span>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${checked ? "bg-emerald-600" : "bg-slate-300"}`}
      >
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${checked ? "translate-x-6" : "translate-x-1"}`} />
      </button>
    </label>
  );
}
function Loading() {
  return (
    <div className="py-12 flex items-center justify-center text-slate-400">
      <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
    </div>
  );
}
